import { Lens } from "@hookform/lenses";
import { Alert, Card, CardContent, CardHeader } from "@mui/material";
import { ReactElement, ReactNode } from "react";

import {
  ShortCodeConfig,
  ShortCodeConfigInput,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import { palette } from "~frontend/theme";
import splitCommaSeparatedList from "~frontend/util/splitCommaSeparatedList";

import { ShortCodeConfigFormValues } from "./types";

export const DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES: ShortCodeConfigFormValues =
  {
    helpKeywords: "",
    helpMessage: "",
    optInKeywords: "",
    optInMessage: "",
    optOutKeywords: "",
    optOutMessage: "",
  };

export function shortCodeConfigToFormValues(
  shortCodeConfig: ShortCodeConfig
): ShortCodeConfigFormValues {
  if (!shortCodeConfig) {
    return DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES;
  }

  return {
    helpKeywords: shortCodeConfig.helpKeywords.join(", "),
    helpMessage: shortCodeConfig.helpMessage,
    optInKeywords: shortCodeConfig.optInKeywords.join(", "),
    optInMessage: shortCodeConfig.optInMessage,
    optOutKeywords: shortCodeConfig.optOutKeywords.join(", "),
    optOutMessage: shortCodeConfig.optOutMessage,
  };
}

export function shortCodeConfigFormValuesToConfig(
  optInConfigFormValues: ShortCodeConfigFormValues
): ShortCodeConfigInput | null {
  return {
    helpKeywords: splitCommaSeparatedList(optInConfigFormValues.helpKeywords),
    helpMessage: optInConfigFormValues.helpMessage,
    optInKeywords: splitCommaSeparatedList(optInConfigFormValues.optInKeywords),
    optInMessage: optInConfigFormValues.optInMessage,
    optOutKeywords: splitCommaSeparatedList(
      optInConfigFormValues.optOutKeywords
    ),
    optOutMessage: optInConfigFormValues.optOutMessage,
  };
}

function MultipleOptInNumbersAlert(): ReactElement {
  return (
    <Alert severity="warning">
      <strong>Warning:</strong> Editing these keywords and messages will affect
      all opt-in phone numbers in this subaccount.
    </Alert>
  );
}

function ShortCodeKeywordAlert(): ReactElement {
  return (
    <Alert severity="warning">
      These keywords and messages MUST match the short code agreement the
      customer has entered into with the carrier. They CANNOT be changed
      unilaterally. If the customer would like to change these keywords or
      messages, they must work with Compliance to update their short code
      agreement. Other keywords can be added by the customer at any time from
      the Short Code page.
    </Alert>
  );
}

export function FormikShortCodeConfig({
  showShortCodeWarning,
  showSharedConfigWarning,
}: {
  showShortCodeWarning?: boolean;
  showSharedConfigWarning?: boolean;
}): ReactElement {
  return (
    <ShortCodeConfigFormWrapper
      showSharedConfigWarning={showSharedConfigWarning}
      showShortCodeWarning={showShortCodeWarning}
    >
      <FormikTextField
        name="shortCodeConfig.optInKeywords"
        label="Opt-In Keywords (comma separated)"
        fullWidth
      />
      <FormikTextField
        name="shortCodeConfig.optInMessage"
        label="Opt-In Message"
        fullWidth
        multiline
      />
      <FormikTextField
        name="shortCodeConfig.optOutKeywords"
        label="Opt Out Keywords (comma separated)"
        fullWidth
      />
      <FormikTextField
        name="shortCodeConfig.optOutMessage"
        label="Opt Out Message"
        fullWidth
        multiline
      />
      <FormikTextField
        name="shortCodeConfig.helpKeywords"
        label="Help Keywords (comma separated)"
        fullWidth
      />
      <FormikTextField
        name="shortCodeConfig.helpMessage"
        label="Help Message"
        fullWidth
        multiline
      />
    </ShortCodeConfigFormWrapper>
  );
}

export function ShortCodeConfigForm({
  lens,
  showShortCodeWarning,
  showSharedConfigWarning,
}: {
  lens: Lens<ShortCodeConfigFormValues>;
  showShortCodeWarning?: boolean;
  showSharedConfigWarning?: boolean;
}): ReactElement {
  return (
    <ShortCodeConfigFormWrapper
      showSharedConfigWarning={showSharedConfigWarning}
      showShortCodeWarning={showShortCodeWarning}
    >
      <RhfTextField
        {...lens.focus("optInKeywords").interop()}
        label="Opt-In Keywords (comma separated)"
        fullWidth
      />
      <RhfTextField
        {...lens.focus("optInMessage").interop()}
        label="Opt-In Message"
        fullWidth
        multiline
      />
      <RhfTextField
        {...lens.focus("optOutKeywords").interop()}
        label="Opt Out Keywords (comma separated)"
        fullWidth
      />
      <RhfTextField
        {...lens.focus("optOutMessage").interop()}
        label="Opt Out Message"
        fullWidth
        multiline
      />
      <RhfTextField
        {...lens.focus("helpKeywords").interop()}
        label="Help Keywords (comma separated)"
        fullWidth
      />
      <RhfTextField
        {...lens.focus("helpMessage").interop()}
        label="Help Message"
        fullWidth
        multiline
      />
    </ShortCodeConfigFormWrapper>
  );
}

function ShortCodeConfigFormWrapper({
  children,
  showShortCodeWarning,
  showSharedConfigWarning,
}: {
  children: ReactNode;
  showShortCodeWarning?: boolean;
  showSharedConfigWarning?: boolean;
}): ReactElement {
  return (
    <Card>
      <CardHeader title="Keywords and Responses" />
      <CardContent sx={{ backgroundColor: palette.lightestGray }}>
        {showShortCodeWarning && <ShortCodeKeywordAlert />}
        {showSharedConfigWarning && <MultipleOptInNumbersAlert />}
        {children}
      </CardContent>
    </Card>
  );
}
